import { memo, MouseEvent, useMemo } from "react";
import {
  EdgeLabelRenderer,
  getBezierPath,
  getSmoothStepPath,
  getStraightPath,
  useReactFlow,
  EdgeProps,
  Position,
  useStore,
} from "reactflow";
import ClickableBaseEdge_ from "./clickableBaseEdge";
import {
  Divider,
  Stack,
  Typography,
  useTheme,
  Box,
  Theme,
  lighten,
} from "@mui/material";
import { convertBitsToBytes } from "../../../utils/easyMaps/convertToBytes";
import { useOutletContext } from "react-router-dom";
import { Close, Download, Upload } from "@mui/icons-material";
import { useStyles } from "./styles";

export const getSpecialPath = (
  { sourceX, sourceY, targetX, targetY }: any,
  offset: number,
) => {
  const centerX = (sourceX + targetX) / 2;
  const centerY = (sourceY + targetY) / 2;

  return `M ${sourceX} ${sourceY} Q ${centerX} ${
    centerY + offset
  } ${targetX} ${targetY}`;
};

interface PositionHandler {
  x: number;
  y: number;
  active?: number;
  location?: {
    long: number;
    lat: number;
  };
}

interface ViewProps {
  theme: Theme;
  data: any;
  isDown: boolean;
  editMode: boolean;
  configLayer: any;
  mapId: string;
  convertInputTraffic: { valor: string | number; unidade: string };
  convertOutputTraffic: { valor: string | number; unidade: string };
  inputPower: number | string;
  outputPower: number | string;
  corTrafficRx?: { cor: string };
  corTrafficTx?: { cor: string };
  corPowerRx?: { cor: string };
  corPowerTx?: { cor: string };
  entangled?: boolean;
  interfaceName?: string;
  styles: any;
  labelScale?: number;
}

export const DetailedView: React.FC<ViewProps> = ({
  theme,
  data,
  isDown,
  editMode,
  configLayer,
  mapId,
  convertInputTraffic,
  convertOutputTraffic,
  inputPower,
  outputPower,
  corTrafficRx,
  corTrafficTx,
  corPowerRx,
  corPowerTx,
  entangled,
  interfaceName,
  styles,
  labelScale,
}) => (
  <Stack sx={styles.stackView(isDown, entangled, false)}>
    <Typography sx={styles.interfaceLabel(theme, labelScale)}>
      {interfaceName && interfaceName?.trim() !== ""
        ? interfaceName
        : data?.interface || "Interface"}
    </Typography>

    {isDown && !editMode ? (
      <Box sx={styles.statusContainer(theme)}>
        <Close sx={{ fontSize: `${12 * (labelScale || 1)}px` }} />
        <Typography component="span" sx={styles.statusText(labelScale)}>
          Offline
        </Typography>
      </Box>
    ) : (
      <>
        {configLayer?.[mapId] === "trafego" && (
          <Stack sx={styles.layerRow()}>
            <Box sx={styles.metricContainer()}>
              <Upload
                sx={{
                  color: corTrafficTx?.cor,
                  fontSize: `${12 * (labelScale || 1)}px`,
                }}
              />
              <Typography
                component="span"
                sx={styles.metricValue(theme, labelScale)}
              >
                {convertOutputTraffic.valor}
              </Typography>
              <Typography
                component="span"
                sx={styles.metricUnit(theme, labelScale)}
              >
                {convertOutputTraffic.unidade}
              </Typography>
            </Box>

            <Divider orientation="vertical" sx={styles.divider(labelScale)} />

            <Box sx={styles.metricContainer()}>
              <Download
                sx={{
                  color: corTrafficRx?.cor,
                  fontSize: `${12 * (labelScale || 1)}px`,
                }}
              />
              <Typography
                component="span"
                sx={styles.metricValue(theme, labelScale)}
              >
                {convertInputTraffic.valor}
              </Typography>
              <Typography
                component="span"
                sx={styles.metricUnit(theme, labelScale)}
              >
                {convertInputTraffic.unidade}
              </Typography>
            </Box>
          </Stack>
        )}

        {configLayer?.[mapId] === "potencia" && (
          <Stack sx={styles.layerRow()}>
            <Box sx={styles.metricContainer()}>
              <Upload
                sx={{
                  color: corPowerTx?.cor,
                  fontSize: `${12 * (labelScale || 1)}px`,
                }}
              />
              <Typography
                component="span"
                sx={styles.metricValue(theme, labelScale)}
              >
                {typeof outputPower === "number"
                  ? outputPower.toFixed(2)
                  : outputPower}
              </Typography>
              <Typography
                component="span"
                sx={styles.metricUnit(theme, labelScale)}
              >
                dBm
              </Typography>
            </Box>

            <Divider orientation="vertical" sx={styles.divider(labelScale)} />
            <Box sx={styles.metricContainer()}>
              <Download
                sx={{
                  color: corPowerRx?.cor,
                  fontSize: `${12 * (labelScale || 1)}px`,
                }}
              />
              <Typography
                component="span"
                sx={styles.metricValue(theme, labelScale)}
              >
                {typeof inputPower === "number"
                  ? inputPower.toFixed(2)
                  : inputPower}
              </Typography>
              <Typography
                component="span"
                sx={styles.metricUnit(theme, labelScale)}
              >
                dBm
              </Typography>
            </Box>
          </Stack>
        )}
      </>
    )}
  </Stack>
);

export const SimpleView: React.FC<ViewProps> = ({
  theme,
  isDown,
  editMode,
  configLayer,
  mapId,
  convertInputTraffic,
  convertOutputTraffic,
  inputPower,
  outputPower,
  corTrafficRx,
  corTrafficTx,
  corPowerRx,
  corPowerTx,
  entangled,
  styles,
  labelScale,
}) => (
  <Stack sx={styles.stackSimpleView(isDown, entangled)}>
    {isDown && !editMode ? (
      <Box sx={styles.statusContainer(theme)}>
        <Close sx={{ fontSize: `${12 * (labelScale || 1)}px` }} />
        <Typography component="span" sx={styles.statusText()}>
          Offline
        </Typography>
      </Box>
    ) : (
      <>
        {configLayer?.[mapId] === "trafego" && (
          <Stack sx={styles.layerRow()}>
            <Box sx={styles.metricContainer()}>
              <Upload
                sx={{
                  color: corTrafficTx?.cor,
                  fontSize: `${12 * (labelScale || 1)}px`,
                }}
              />
              <Typography
                component="span"
                sx={styles.metricValue(theme, labelScale)}
              >
                {convertOutputTraffic.valor}
              </Typography>
              <Typography
                component="span"
                sx={styles.metricUnit(theme, labelScale)}
              >
                {convertOutputTraffic.unidade}
              </Typography>
            </Box>

            <Divider orientation="vertical" sx={styles.divider(labelScale)} />

            <Box sx={styles.metricContainer()}>
              <Download
                sx={{
                  color: corTrafficRx?.cor,
                  fontSize: `${12 * (labelScale || 1)}px`,
                }}
              />
              <Typography
                component="span"
                sx={styles.metricValue(theme, labelScale)}
              >
                {convertInputTraffic.valor}
              </Typography>
              <Typography
                component="span"
                sx={styles.metricUnit(theme, labelScale)}
              >
                {convertInputTraffic.unidade}
              </Typography>
            </Box>
          </Stack>
        )}

        {configLayer?.[mapId] === "potencia" && (
          <Stack sx={styles.layerRow()}>
            <Box sx={styles.metricContainer()}>
              <Upload
                sx={{
                  color: corPowerTx?.cor,
                  fontSize: `${12 * (labelScale || 1)}px`,
                }}
              />
              <Typography
                component="span"
                sx={styles.metricValue(theme, labelScale)}
              >
                {typeof outputPower === "number"
                  ? outputPower.toFixed(2)
                  : outputPower}
              </Typography>
              <Typography
                component="span"
                sx={styles.metricUnit(theme, labelScale)}
              >
                dBm
              </Typography>
            </Box>

            <Divider orientation="vertical" sx={styles.divider(labelScale)} />

            <Box sx={styles.metricContainer()}>
              <Download
                sx={{
                  color: corPowerRx?.cor,
                  fontSize: `${12 * (labelScale || 1)}px`,
                }}
              />
              <Typography
                component="span"
                sx={styles.metricValue(theme, labelScale)}
              >
                {typeof inputPower === "number"
                  ? inputPower.toFixed(2)
                  : inputPower}
              </Typography>
              <Typography
                component="span"
                sx={styles.metricUnit(theme, labelScale)}
              >
                dBm
              </Typography>
            </Box>
          </Stack>
        )}
      </>
    )}
  </Stack>
);

export const CompactView: React.FC<ViewProps> = ({
  theme,
  isDown,
  editMode,
  configLayer,
  mapId,
  convertInputTraffic,
  convertOutputTraffic,
  inputPower,
  outputPower,
  corTrafficRx,
  corTrafficTx,
  corPowerRx,
  corPowerTx,
  entangled,
  styles,
  labelScale,
}) => (
  <Box sx={styles.stackView(isDown, entangled, true)}>
    {isDown && !editMode ? (
      <Close
        sx={{
          fontSize: `${14 * (labelScale || 1)}px`,
          color: theme.palette.error.main,
        }}
      />
    ) : (
      <>
        {configLayer?.[mapId] === "trafego" && (
          <Stack sx={styles.layerRow()}>
            <Box sx={styles.metricContainer()}>
              <Typography
                component="span"
                sx={styles.compactValue(corTrafficTx?.cor, labelScale)}
              >
                {convertOutputTraffic.valor}
              </Typography>
              <Typography
                component="span"
                sx={styles.metricUnit(theme, labelScale)}
              >
                {convertOutputTraffic.unidade?.[0]}
              </Typography>
            </Box>

            <Divider orientation="vertical" sx={styles.divider(labelScale)} />

            <Box sx={styles.metricContainer()}>
              <Typography
                component="span"
                sx={styles.compactValue(corTrafficRx?.cor, labelScale)}
              >
                {convertInputTraffic.valor}
              </Typography>
              <Typography
                component="span"
                sx={styles.metricUnit(theme, labelScale)}
              >
                {convertInputTraffic.unidade?.[0]}
              </Typography>
            </Box>
          </Stack>
        )}

        {configLayer?.[mapId] === "potencia" && (
          <Stack sx={styles.layerRow()}>
            <Box sx={styles.metricContainer()}>
              <Typography
                component="span"
                sx={styles.compactValue(corPowerTx?.cor)}
              >
                {typeof outputPower === "number"
                  ? outputPower.toFixed(2)
                  : outputPower}
              </Typography>
              <Typography
                component="span"
                sx={styles.metricUnit(theme, labelScale)}
              >
                d
              </Typography>
            </Box>

            <Divider orientation="vertical" sx={styles.divider(labelScale)} />

            <Box sx={styles.metricContainer()}>
              <Typography
                component="span"
                sx={styles.compactValue(corPowerRx?.cor)}
              >
                {typeof inputPower === "number"
                  ? inputPower.toFixed(2)
                  : inputPower}
              </Typography>{" "}
              <Typography
                component="span"
                sx={styles.metricUnit(theme, labelScale)}
              >
                d
              </Typography>
            </Box>
          </Stack>
        )}
      </>
    )}
  </Box>
);

const ClickableBaseEdge = ClickableBaseEdge_ as any;

interface EdgeAnimationProps {
  path: string;
  stroke: string;
  larguraLinha: number;
  isRx: boolean;
  mode: string;
}

const EdgeAnimation = memo(
  ({ path, stroke, larguraLinha, isRx, mode }: EdgeAnimationProps) => {
    return (
      <g>
        <animateMotion
          dur="1.5s"
          repeatCount="indefinite"
          calcMode="linear"
          {...(isRx ? { keyTimes: "0;1", keyPoints: "1;0" } : {})}
          path={path}
        />
        <circle
          r={larguraLinha > 7 ? larguraLinha * 1.6 : 10}
          fill={stroke}
          opacity="0.15"
        />
        <circle
          r={larguraLinha > 7 ? larguraLinha * 1.0 : 8}
          fill={stroke}
          opacity="0.4"
        />
        <circle
          r={larguraLinha > 7 ? larguraLinha * 0.5 : 5}
          fill={mode === "dark" ? lighten(stroke, 0.3) : lighten(stroke, 0.6)}
          style={{
            filter: `drop-shadow(0 0 8px ${stroke})`,
          }}
        />
      </g>
    );
  },
);

EdgeAnimation.displayName = "EdgeAnimation";

const LABEL_SIZE_MIN = 1;
const LABEL_SIZE_MAX = 50;
const LABEL_SCALE_MAX = 6;

const computeLabelScale = (size: number) => {
  const clamped = Math.min(
    Math.max(Number(size) || LABEL_SIZE_MIN, LABEL_SIZE_MIN),
    LABEL_SIZE_MAX,
  );
  return (
    1 +
    ((clamped - LABEL_SIZE_MIN) * (LABEL_SCALE_MAX - 1)) /
      (LABEL_SIZE_MAX - LABEL_SIZE_MIN)
  );
};

const PositionableEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  markerEnd,
  data,
  selected,
}: EdgeProps) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const isModeLight = theme.palette.mode === "light";
  const styles = useStyles(theme, isModeLight);
  const { configLayer } = useOutletContext<any>();
  const { getEdges, setEdges, screenToFlowPosition } = useReactFlow();
  const isAnyEdgeSelected = useStore((store: any) =>
    store.edges.some((edge: any) => edge.selected),
  );
  const {
    mapId,
    editMode,
    entangled,
    lineType,
    viewType,
    edgeLabelSize,
    mapRef,
    animation = "desativada",
    showLabelEnlace,
    easyMapsConfig,
    variant = "trafego",
    corTrafficTx,
    corTrafficRx,
    corPowerTx,
    corPowerRx,
    corTraffic,
    corPower,
    isDown,
    interfaceName,
  } = data;

  const edges = getEdges();
  const positionHandlers: PositionHandler[] = data?.positionHandlers ?? [];

  const handlersKey = positionHandlers.map((h) => `${h.x},${h.y}`).join(";");
  const colors = useMemo(() => {
    if (variant === "trafego") {
      return corTraffic?.cor || "gray";
    } else if (variant === "potencia") {
      return corPower?.cor || "gray";
    }
    return "gray";
  }, [data, id, variant]);

  const convertOutputTraffic = useMemo(
    () => convertBitsToBytes(data?.tx_traffic),
    [data],
  );
  const convertInputTraffic = useMemo(
    () => convertBitsToBytes(data?.rx_traffic),
    [data],
  );
  const inputPower = useMemo(
    () => (isNaN(data?.rx_power) ? "-" : data?.rx_power),
    [data],
  );
  const outputPower = useMemo(
    () => (isNaN(data?.tx_power) ? "-" : data?.tx_power),
    [data],
  );

  const viewTypeLabel =
    viewType && viewType.trim() !== ""
      ? viewType
      : easyMapsConfig?.configuracoes?.viewType;

  const resolvedEdgeLabelSize =
    edgeLabelSize && Number(edgeLabelSize) > 0
      ? Number(edgeLabelSize)
      : easyMapsConfig?.configuracoes?.edgeLabelSize || 0;

  const labelScale = computeLabelScale(resolvedEdgeLabelSize);

  const lineAnimation =
    easyMapsConfig?.configuracoes?.animacao?.corredorLinha ?? true;

  const { txPath, rxPath, midPoint } = useMemo(() => {
    let pathFunction: any;

    switch (lineType) {
      case "reta":
        pathFunction = getStraightPath;
        break;
      case "ortogonal":
        pathFunction = getSmoothStepPath;
        break;
      case "curvada":
        pathFunction = getBezierPath;
        break;
      default:
        pathFunction = getStraightPath;
    }

    const polylinePoints = [
      { x: sourceX, y: sourceY },
      ...positionHandlers.map((h) => ({ x: h.x, y: h.y })),
      { x: targetX, y: targetY },
    ];

    const { midPoint, midSegmentIndex, midRatio } = (() => {
      const lens = [];
      let total = 0;
      for (let i = 0; i < polylinePoints.length - 1; i++) {
        const dx = polylinePoints[i + 1].x - polylinePoints[i].x;
        const dy = polylinePoints[i + 1].y - polylinePoints[i].y;
        const len = Math.hypot(dx, dy);
        lens.push(len);
        total += len;
      }
      if (total === 0) {
        return { midPoint: polylinePoints[0], midSegmentIndex: 0, midRatio: 0 };
      }
      const half = total / 2;
      let acc = 0;
      for (let i = 0; i < lens.length; i++) {
        if (acc + lens[i] >= half) {
          const ratio = lens[i] === 0 ? 0 : (half - acc) / lens[i];
          const p0 = polylinePoints[i];
          const p1 = polylinePoints[i + 1];
          return {
            midPoint: {
              x: p0.x + (p1.x - p0.x) * ratio,
              y: p0.y + (p1.y - p0.y) * ratio,
            },
            midSegmentIndex: i,
            midRatio: ratio,
          };
        }
        acc += lens[i];
      }
      const lastIndex = polylinePoints.length - 1;
      return {
        midPoint: polylinePoints[lastIndex],
        midSegmentIndex: Math.max(lens.length - 1, 0),
        midRatio: 1,
      };
    })();

    const buildEdge = (p0: any, p1: any, isFirst: boolean, isLast: boolean) => {
      const segDifX = p1.x - p0.x;
      const _sourcePosition = isFirst
        ? sourcePosition
        : segDifX < 0
          ? "left"
          : "right";
      const _targetPosition = isLast
        ? targetPosition
        : segDifX < 0
          ? "right"
          : "left";
      // getSmoothStepPath creates loops when nodes are axis-aligned with the
      // handle direction (no room for the orthogonal step). Fall back to straight.
      const segDifY = p1.y - p0.y;
      const isVerticalHandle =
        _sourcePosition === "bottom" || _sourcePosition === "top";
      const isHorizontalHandle =
        _sourcePosition === "left" || _sourcePosition === "right";
      const ALIGN_THRESHOLD = 3;
      const isDegenerate =
        pathFunction === getSmoothStepPath &&
        ((isVerticalHandle && Math.abs(segDifX) < ALIGN_THRESHOLD) ||
          (isHorizontalHandle && Math.abs(segDifY) < ALIGN_THRESHOLD));

      const fn = isDegenerate ? getStraightPath : pathFunction;
      const [edgePath, labelX, labelY] = fn({
        sourceX: p0.x,
        sourceY: p0.y,
        sourcePosition: _sourcePosition as Position,
        targetX: p1.x,
        targetY: p1.y,
        targetPosition: _targetPosition as Position,
      });
      return { edgePath, labelX, labelY };
    };

    const txPath = [];
    const rxPath = [];
    const lastSegmentIndex = polylinePoints.length - 2;
    for (let i = 0; i <= lastSegmentIndex; i++) {
      const from = polylinePoints[i];
      const to = polylinePoints[i + 1];
      const isFirst = i === 0;
      const isLast = i === lastSegmentIndex;
      if (i < midSegmentIndex) {
        txPath.push({ ...buildEdge(from, to, isFirst, false), insertIndex: i });
      } else if (i > midSegmentIndex) {
        rxPath.push({ ...buildEdge(from, to, false, isLast), insertIndex: i });
      } else if (midRatio > 0 && midRatio < 1) {
        txPath.push({
          ...buildEdge(from, midPoint, isFirst, false),
          insertIndex: i,
        });
        rxPath.push({
          ...buildEdge(midPoint, to, false, isLast),
          insertIndex: i,
        });
      } else if (midRatio === 1) {
        txPath.push({
          ...buildEdge(from, to, isFirst, isLast),
          insertIndex: i,
        });
      } else {
        rxPath.push({
          ...buildEdge(from, to, isFirst, isLast),
          insertIndex: i,
        });
      }
    }

    return { txPath, rxPath, midPoint };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    lineType,
    handlersKey,
  ]);

  const baseOpacity = entangled ? 1 : 0.33;
  const edgeOpacity = isAnyEdgeSelected && !selected ? 0.4 : baseOpacity;
  const larguraLinha = editMode
    ? 4
    : Math.max(1, easyMapsConfig?.configuracoes?.larguraEnlace ?? 6);

  const handleMouseMove = (event: any, id: string, handlerIndex: number) => {
    let activeEdge = parseInt(event.target.dataset.active ?? "-1");
    if (activeEdge === -1) {
      return;
    }
    if (activeEdge === 1) {
      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      const edgeIndex = edges.findIndex((edge) => edge.id === id);
      let latlng = mapRef?.containerPointToLatLng([position.x, position.y]);
      setEdges((prevEdges: any[]) => {
        const edgeIndex = prevEdges.findIndex((edge) => edge.id === id);
        if (edgeIndex === -1) return prevEdges;
        const newEdges = [...prevEdges];
        const newHandlers = [
          ...(newEdges[edgeIndex].data.positionHandlers || []),
        ];

        newHandlers[handlerIndex] = mapRef
          ? {
              x: position.x,
              y: position.y,
              active: 1,
              location: {
                long: latlng.lng,
                lat: latlng.lat,
              },
            }
          : {
              x: position.x,
              y: position.y,
              active: 1,
            };

        newEdges[edgeIndex] = {
          ...newEdges[edgeIndex],
          data: {
            ...newEdges[edgeIndex].data,
            positionHandlers: newHandlers,
          },
        };
        return newEdges;
      });
    }
  };
  const handleMouseUp = () => {
    if (editMode) {
      setEdges((prevEdges: any[]) => {
        return prevEdges.map((edge) => {
          if (!edge.data?.positionHandlers?.some((h: any) => h.active !== -1))
            return edge;
          return {
            ...edge,
            data: {
              ...edge.data,
              positionHandlers: edge.data.positionHandlers?.map(
                (handler: any) => ({
                  ...handler,
                  active: -1,
                }),
              ),
            },
          };
        });
      });
    }
    return;
  };

  const handleMouseDown = (
    event: any,
    id: string,
    handlerIndex: number,
    active: number,
  ) => {
    if (editMode) {
      setEdges((prevEdges: any[]) => {
        const edgeIndex = prevEdges.findIndex((edge) => edge.id === id);
        if (edgeIndex === -1) return prevEdges;
        const newEdges = [...prevEdges];
        const newHandlers = [
          ...(newEdges[edgeIndex].data.positionHandlers || []),
        ];

        newHandlers[handlerIndex] = { ...newHandlers[handlerIndex], active: 1 };

        newEdges[edgeIndex] = {
          ...newEdges[edgeIndex],
          data: {
            ...newEdges[edgeIndex].data,
            positionHandlers: newHandlers,
          },
        };
        return newEdges;
      });
      event.preventDefault();
    }
    event.preventDefault();
    return;
  };

  const handleDeleteEdge = (event: any, id: string, handlerIndex: number) => {
    event.preventDefault();
    if (editMode) {
      setEdges((prevEdges: any[]) => {
        const edgeIndex = prevEdges.findIndex((edge) => edge.id === id);
        if (edgeIndex === -1) return prevEdges;

        const newEdges = [...prevEdges];
        const newHandlers = [
          ...(newEdges[edgeIndex].data.positionHandlers || []),
        ];
        newHandlers.splice(handlerIndex, 1);

        newEdges[edgeIndex] = {
          ...newEdges[edgeIndex],
          data: {
            ...newEdges[edgeIndex].data,
            positionHandlers: newHandlers,
          },
        };
        return newEdges;
      });
    }
    return;
  };

  const handleCreateEdge = (event: any, id: string, index: number) => {
    if (selected && editMode) {
      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      let latlng = mapRef?.containerPointToLatLng([position.x, position.y]);

      setEdges((prevEdges: any[]) => {
        const edgeIndex = prevEdges.findIndex((edge) => edge.id === id);
        if (edgeIndex === -1) return prevEdges;

        const newEdges = [...prevEdges];
        const newHandlers = [
          ...(newEdges[edgeIndex].data.positionHandlers || []),
        ];

        newHandlers.splice(
          index,
          0,
          mapRef
            ? {
                x: position.x,
                y: position.y,
                active: -1,
                location: {
                  long: latlng.lng,
                  lat: latlng.lat,
                },
              }
            : {
                x: position.x,
                y: position.y,
                active: -1,
              },
        );

        newEdges[edgeIndex] = {
          ...newEdges[edgeIndex],
          data: {
            ...newEdges[edgeIndex].data,
            positionHandlers: newHandlers,
          },
        };
        return newEdges;
      });
    }
  };

  const positionLabel = midPoint;

  const viewProps: ViewProps = {
    theme,
    data,
    isDown,
    editMode,
    configLayer,
    mapId,
    convertInputTraffic,
    convertOutputTraffic,
    inputPower,
    outputPower,
    corTrafficRx,
    corTrafficTx,
    corPowerRx,
    corPowerTx,
    entangled,
    interfaceName,
    styles,
    labelScale,
  };

  const renderView = () => {
    switch (viewTypeLabel) {
      case "compact":
        return <CompactView {...viewProps} />;
      case "simple":
        return <SimpleView {...viewProps} />;
      case "detailed":
      default:
        return <DetailedView {...viewProps} />;
    }
  };

  const txStroke = selected
    ? (theme.palette as any).color.zabbixSeverty.selectedTranslucid
    : editMode
      ? "#5e5e5e"
      : variant === "potencia"
        ? data?.corPowerTx?.cor || colors
        : data?.corTrafficTx?.cor || colors;

  const rxStroke = selected
    ? (theme.palette as any).color.zabbixSeverty.selectedTranslucid
    : editMode
      ? "#5e5e5e"
      : variant === "potencia"
        ? data?.corPowerRx?.cor || colors
        : data?.corTrafficRx?.cor || colors;

  return (
    <>
      {rxPath.map(({ edgePath, insertIndex }, index) => (
        <ClickableBaseEdge
          onDoubleClick={(event: any) =>
            handleCreateEdge(event, id, insertIndex)
          }
          key={`edge${id}_rx_${index}`}
          path={edgePath as string}
          markerEnd={
            index === rxPath.length - 1 ? (markerEnd as string) : "false"
          }
          className={isDown && !editMode ? "edge-pulse" : ""}
          style={{
            ...styles.clickableBaseEdge(
              selected,
              editMode,
              colors,
              edgeOpacity,
              larguraLinha,
              animation,
              isDown,
              easyMapsConfig,
            ),
            stroke: rxStroke,
            animationDirection: isDown ? "normal" : "reverse",
          }}
        />
      ))}
      {lineAnimation &&
        animation !== "desativada" &&
        !isDown &&
        !editMode &&
        ((variant === "trafego" &&
          !isNaN(data?.rx_traffic) &&
          data?.rx_traffic !== 0) ||
          (variant === "potencia" &&
            !isNaN(data?.rx_power) &&
            data?.rx_power !== 0)) &&
        rxPath.length > 0 && (
          <EdgeAnimation
            key={`rx-anim-group-${id}`}
            path={rxPath.map((p) => p.edgePath).join(" ")}
            stroke={rxStroke}
            larguraLinha={larguraLinha}
            isRx={true}
            mode={mode}
          />
        )}
      {txPath.map(({ edgePath, insertIndex }, index) => (
        <ClickableBaseEdge
          onDoubleClick={(event: any) =>
            handleCreateEdge(event, id, insertIndex)
          }
          key={`edge${id}_tx_${index}`}
          path={edgePath as string}
          markerEnd={"false"}
          className={isDown && !editMode ? "edge-pulse" : ""}
          style={{
            ...styles.clickableBaseEdge(
              selected,
              editMode,
              colors,
              edgeOpacity,
              larguraLinha,
              animation,
              isDown,
              easyMapsConfig,
            ),
            stroke: txStroke,
          }}
        />
      ))}

      {lineAnimation &&
        animation !== "desativada" &&
        !isDown &&
        !editMode &&
        ((variant === "trafego" &&
          !isNaN(data?.tx_traffic) &&
          data?.tx_traffic !== 0) ||
          (variant === "potencia" &&
            !isNaN(data?.tx_power) &&
            data?.tx_power !== 0)) &&
        txPath.length > 0 && (
          <EdgeAnimation
            key={`tx-anim-group-${id}`}
            path={txPath.map((p) => p.edgePath).join(" ")}
            stroke={txStroke}
            larguraLinha={larguraLinha}
            isRx={false}
            mode={mode}
          />
        )}

      {!editMode && showLabelEnlace && (
        <EdgeLabelRenderer key={`edge${id}_label`}>
          <div
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${positionLabel?.x}px,${positionLabel?.y}px)`,
              pointerEvents: "all",
              zIndex: 1000,
            }}
          >
            {renderView()}
          </div>
        </EdgeLabelRenderer>
      )}

      {editMode &&
        selected &&
        positionHandlers.map(({ x, y, active }, handlerIndex) => (
          <EdgeLabelRenderer key={`edge${id}_handler${handlerIndex}`}>
            <div
              data-active={active ?? -1}
              style={styles.divEdge(active as number, x, y)}
              className="nodrag nopan"
              onMouseMove={(event) => handleMouseMove(event, id, handlerIndex)}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onDoubleClick={(event: MouseEvent<HTMLDivElement>) =>
                event.stopPropagation()
              }
            >
              <button
                data-active={active ?? -1}
                style={styles.buttonDraggable(active as number)}
                className="edgebutton"
                onMouseDown={(event) =>
                  handleMouseDown(event, id, handlerIndex, active as number)
                }
                onContextMenu={(event) =>
                  handleDeleteEdge(event, id, handlerIndex)
                }
              ></button>
            </div>
          </EdgeLabelRenderer>
        ))}
    </>
  );
};

export default memo(PositionableEdge);
